import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/api/client'
import { jobApi, jobPortalApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { TextField } from '@/components/ui/Field'
import { Sheet } from '@/components/ui/Sheet'
import { StatStrip } from '@/components/ui/StatStrip'
import { StatusPill, type Tone } from '@/components/ui/StatusPill'
import { Switch } from '@/components/ui/Toggle'
import { cn, formatDate, initials, relativeTime } from '@/lib/utils'
import { toast } from '@/store/toast'
import type { JobPortal, JobPortalUpdate, JobSourceSummary } from '@/types/api'

import { SectionCard, useIsPhone } from './shared'

const PORTALS_KEY = ['job-portals'] as const
const POLL_MS = 4000

/**
 * The newer portal endpoints, called here until `api/endpoints.ts` (another
 * owner's file) lists them: the user's own login, and a test run.
 */
const portalExtras = {
  credentials: (key: string, body: { identifier: string; password: string }) =>
    api.put<JobPortal>(`/job-portals/${encodeURIComponent(key)}/credentials`, body),
  rehearse: (key: string) => api.post<JobPortal>(`/job-portals/${encodeURIComponent(key)}/rehearse`),
}
/** How long a background run is followed before we stop asking. */
const WATCH_MS = 3 * 60_000
const NO_PORTALS: JobPortal[] = []

// ---------------------------------------------------------------------------
// Cache helpers: every mutation answers with the portal, so the list is patched
// in place rather than refetched.
// ---------------------------------------------------------------------------
function putPortal(queryClient: QueryClient, portal: JobPortal | null | undefined) {
  // An answer that is not a portal (an older server, say): ask for the list again.
  if (!portal || typeof portal.key !== 'string') {
    queryClient.invalidateQueries({ queryKey: PORTALS_KEY })
    return
  }
  queryClient.setQueryData<JobPortal[]>(PORTALS_KEY, (current) => {
    if (!Array.isArray(current)) return current
    return current.some((entry) => entry.key === portal.key)
      ? current.map((entry) => (entry.key === portal.key ? portal : entry))
      : [...current, portal]
  })
}

function patchPortal(queryClient: QueryClient, key: string, patch: Partial<JobPortal>) {
  queryClient.setQueryData<JobPortal[]>(PORTALS_KEY, (current) =>
    Array.isArray(current)
      ? current.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry))
      : current,
  )
}

/** New jobs landed: everything that lists or counts jobs is out of date. */
function refreshJobLists(queryClient: QueryClient) {
  for (const key of ['job-sources', 'top-matches', 'jobs', 'job-facets', 'recommendations']) {
    queryClient.invalidateQueries({ queryKey: [key] })
  }
}

/** What moves when a background run finishes. */
const fingerprint = (portal: JobPortal) =>
  [portal.status, portal.lastSyncedAt, portal.lastError, portal.jobCount, portal.accountProfile ?? '', portal.lastRehearsal?.at ?? ''].join('|')

/** A background run we are following: over once the portal's fingerprint moves. */
interface Watch {
  kind: 'sync' | 'connect' | 'rehearse'
  baseline: string
  until: number
}

/** Sites need an account unless the server says otherwise; feeds never do. */
const usesAccount = (portal: JobPortal) => portal.kind === 'site' && portal.needsAccount !== false
/** Reads fine without signing in, but applying there needs an account (Reed, Dice). */
const accountToApply = (portal: JobPortal) =>
  portal.kind === 'site' && portal.needsAccount === false && portal.canApplyOnPortal

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return url
  }
}

const plural = (count: number, word: string) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------
export function PortalsTab() {
  const queryClient = useQueryClient()
  const [watches, setWatches] = useState<Record<string, Watch>>({})
  const [tick, setTick] = useState(0)
  const watching = Object.keys(watches).length > 0

  const portalsQuery = useQuery({
    queryKey: PORTALS_KEY,
    queryFn: jobPortalApi.list,
    // Only while something is running in the background; otherwise no polling.
    refetchInterval: (query) => {
      const data = query.state.data
      const connecting = Array.isArray(data) && data.some((portal) => portal.status === 'connecting')
      return connecting || watching ? POLL_MS : false
    },
  })
  const loaded = Array.isArray(portalsQuery.data)
  const all = loaded ? (portalsQuery.data as JobPortal[]) : NO_PORTALS
  // Naukri's account is made here too, but it lives under "Already connected", not the catalogue.
  const portals = all.filter((portal) => !portal.accountOnly)
  const naukriSignup = all.find((portal) => portal.accountOnly && portal.key === 'naukri') ?? null

  // Drop a watch once its run has finished (or it has been long enough).
  useEffect(() => {
    const keys = Object.keys(watches)
    if (keys.length === 0) return
    const now = Date.now()
    // Read the cache itself: a mutation writes it before this render's copy catches up,
    // and a watch must be compared against the portal it was started from.
    const cached = queryClient.getQueryData<JobPortal[]>(PORTALS_KEY)
    const latest = Array.isArray(cached) ? cached : portals
    const finished = keys.filter((key) => {
      const portal = latest.find((entry) => entry.key === key)
      const watch = watches[key]
      return !portal || !watch || now >= watch.until || fingerprint(portal) !== watch.baseline
    })
    if (finished.length > 0) {
      const fetched = finished.some((key) => watches[key]?.kind === 'sync')
      setWatches((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !finished.includes(key))),
      )
      if (fetched) refreshJobLists(queryClient)
      return
    }
    const nearest = Math.min(...keys.map((key) => watches[key]?.until ?? now))
    const timer = window.setTimeout(() => setTick((n) => n + 1), Math.max(nearest - now, 0) + 50)
    return () => window.clearTimeout(timer)
  }, [watches, portals, tick, queryClient])

  const watch = (portal: JobPortal, kind: Watch['kind']) =>
    setWatches((current) => ({
      ...current,
      [portal.key]: { kind, baseline: fingerprint(portal), until: Date.now() + WATCH_MS },
    }))

  const selected = portals.filter((portal) => portal.selected).length
  const connected = portals.filter((portal) => portal.status === 'connected').length
  const needsYou = portals.filter((portal) => portal.status === 'needs_you' || portal.status === 'error').length
  const connecting = portals.some((portal) => portal.status === 'connecting')
  const jobsFound = portals.reduce((sum, portal) => sum + (portal.jobCount ?? 0), 0)

  const loading = !loaded && portalsQuery.isFetching
  const failed = !loaded && !loading && portalsQuery.isError

  return (
    <div className="space-y-4">
      <SectionCard
        id="profile-portals"
        title="Job portals"
        description="Choose where we look for jobs and apply for you. We create your account with your private application email, set up your profile from this page, and bring matching jobs into your feed."
      >
        <StatStrip
          loading={loading}
          segments={[
            { key: 'selected', label: 'Selected', value: selected, tone: 'brand' },
            { key: 'connected', label: 'Connected', value: connected, tone: 'success', live: connecting },
            { key: 'needsYou', label: 'Needs you', value: needsYou, tone: 'warning' },
            { key: 'jobs', label: 'Jobs found', value: jobsFound.toLocaleString(), tone: 'info' },
          ]}
        />
      </SectionCard>

      <SourceSummary />

      <AlreadyConnected naukriSignup={naukriSignup} />

      {loading ? (
        <div className="card divide-y divide-line" aria-busy="true" aria-label="Loading job portals">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
              <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-2/5" />
                <Skeleton className="h-3 w-3/5" />
              </div>
              <Skeleton className="h-6 w-11 shrink-0 rounded-pill" />
            </div>
          ))}
        </div>
      ) : failed ? (
        <div className="card">
          <ErrorState
            title="Job portals are unavailable"
            description="We could not load your job portals. Nothing was changed; try again in a moment."
            onRetry={() => portalsQuery.refetch()}
          />
        </div>
      ) : (
        <>
          {portals.length === 0 ? (
            <div className="card">
              <EmptyState
                title="No portals yet"
                description="Add a portal below and we will learn how to read its job listings."
              />
            </div>
          ) : (
            <section className="card overflow-hidden" aria-labelledby="portals-catalogue-title">
              <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 sm:px-5">
                <h3 id="portals-catalogue-title" className="text-[15px] font-semibold text-ink">
                  Catalogue
                </h3>
                <span className="text-[12.5px] text-ink-muted">
                  <span className="num font-medium text-ink">{portals.length}</span> portals
                </span>
              </header>
              <ul className="divide-y divide-line">
                {portals.map((portal) => (
                  <PortalRow
                    key={portal.key}
                    portal={portal}
                    fetching={watches[portal.key]?.kind === 'sync'}
                    onWatch={watch}
                  />
                ))}
              </ul>
            </section>
          )}
          <AddPortalCard onWatch={watch} />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Naukri and employer career pages: connected elsewhere, shown for the full picture
// ---------------------------------------------------------------------------
/**
 * Every source of jobs with its numbers, so it is plain where matches come
 * from and where applications went: on the portal itself, or on the
 * employer's own site when the portal only points there.
 */
function SourceSummary() {
  const summary = useQuery({ queryKey: ['job-portals-summary'], queryFn: jobPortalApi.summary, refetchInterval: 60_000, retry: false })
  const rows: JobSourceSummary[] = Array.isArray(summary.data) ? summary.data : []
  if (summary.isLoading) return <Skeleton className="h-24 w-full" />
  if (!rows.length) return null
  const total = rows.reduce(
    (sum, row) => ({ matches: sum.matches + row.matches, applications: sum.applications + row.applications, submitted: sum.submitted + row.submitted }),
    { matches: 0, applications: 0, submitted: 0 },
  )
  return (
    <SectionCard
      id="profile-portal-summary"
      title="Where your jobs and applications come from"
      description={`${total.matches.toLocaleString()} open matches and ${total.applications.toLocaleString()} applications (${total.submitted.toLocaleString()} submitted) across ${rows.length} source${rows.length === 1 ? '' : 's'}.`}
    >
      <div className="-mx-4 overflow-x-auto sm:mx-0">
        <table className="w-full min-w-[560px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[11.5px] uppercase tracking-wide text-ink-faint">
              <th className="px-4 py-2 font-medium sm:px-3">Source</th>
              <th className="px-3 py-2 text-right font-medium">Matches</th>
              <th className="px-3 py-2 text-right font-medium">Applied</th>
              <th className="px-3 py-2 font-medium">Applied where</th>
              <th className="px-3 py-2 text-right font-medium">Submitted</th>
              <th className="px-3 py-2 text-right font-medium">Needs you</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-line last:border-0">
                <td className="px-4 py-2.5 font-medium text-ink sm:px-3">{row.label}</td>
                <td className="num px-3 py-2.5 text-right text-ink-soft">{row.matches.toLocaleString()}</td>
                <td className="num px-3 py-2.5 text-right text-ink-soft">{row.applications.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-ink-muted">
                  {row.applications === 0
                    ? '—'
                    : [
                        row.appliedOnPortal > 0 ? `${row.appliedOnPortal} on ${row.label}` : null,
                        row.appliedOnCompanySite > 0 ? `${row.appliedOnCompanySite} on the company's site` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                </td>
                <td className="num px-3 py-2.5 text-right text-success">{row.submitted.toLocaleString()}</td>
                <td className="num px-3 py-2.5 text-right text-warning">{row.needsYou.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}

function AlreadyConnected({ naukriSignup }: { naukriSignup: JobPortal | null }) {
  // The same query the dashboard's "Pull jobs from" row reads.
  const sourcesQuery = useQuery({
    queryKey: ['job-sources'],
    queryFn: jobApi.sources,
    staleTime: 30_000,
  })
  const sources = Array.isArray(sourcesQuery.data) ? sourcesQuery.data : []
  const naukri = sources.find((option) => option.source === 'naukri' && !option.board)
  const careerPages = sources.find((option) => option.source === 'internal' && !option.board)
  // The account we are making on Naukri ("Create one for me", or set up after onboarding).
  const signupRunning = naukriSignup?.selected === true && naukriSignup.status === 'connecting'
  const signupWaiting = naukriSignup?.selected === true && Boolean(naukriSignup.handoffUrl)

  return (
    <section aria-labelledby="portals-existing-title" className="card overflow-hidden">
      <h3 id="portals-existing-title" className="label-eyebrow border-b border-line px-4 py-3 sm:px-5">
        Already connected
      </h3>
      <ul className="divide-y divide-line md:grid md:grid-cols-2 md:divide-x md:divide-y-0">
        <ExistingSource
          name="Naukri"
          detail={
            naukri?.connected
              ? `Connected · ${plural(naukri.jobCount, 'job')}`
              : signupWaiting
                ? 'Needs you: finish a step on Naukri'
                : signupRunning
                  ? 'Creating your account…'
                  : naukri
                    ? 'Not connected'
                    : 'Managed in Settings'
          }
          live={Boolean(naukri?.connected) || signupRunning}
          to={signupWaiting && naukriSignup?.handoffUrl ? naukriSignup.handoffUrl : '/dashboard/settings#job-boards'}
          action={signupWaiting ? 'Open' : naukri && !naukri.connected && !signupRunning ? 'Connect or create' : 'Manage'}
        />
        <ExistingSource
          name="Company career pages"
          detail={careerPages ? `Always on · ${plural(careerPages.jobCount, 'job')}` : 'Always on'}
          live
          to="/dashboard/jobs?source=internal"
          action="View jobs"
        />
      </ul>
    </section>
  )
}

function ExistingSource({
  name,
  detail,
  live,
  to,
  action,
}: {
  name: string
  detail: string
  live: boolean
  to: string
  action: string
}) {
  return (
    <li className="flex min-w-0 items-center gap-3 px-4 py-3 sm:px-5">
      <span
        aria-hidden
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-surface-muted text-[12px] font-semibold text-ink-soft"
      >
        {initials(name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="break-words text-[14px] font-medium leading-snug text-ink">{name}</p>
        <p className="mt-0.5 flex items-center gap-1.5 truncate text-[12.5px] text-ink-muted">
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full', live ? 'bg-success' : 'bg-line-strong')}
          />
          <span className="num truncate">{detail}</span>
        </p>
      </div>
      <Link
        to={to}
        className="inline-flex h-9 shrink-0 items-center rounded-field px-2.5 text-[13px] font-medium text-brand transition-colors hover:bg-brand-soft"
      >
        {action}
      </Link>
    </li>
  )
}

// ---------------------------------------------------------------------------
// One portal: the row, and what each action does
// ---------------------------------------------------------------------------
/** Every mutation a portal row can fire; the cache is patched from each answer. */
function usePortalActions(portal: JobPortal, onWatch: (portal: JobPortal, kind: Watch['kind']) => void) {
  const queryClient = useQueryClient()

  const update = useMutation({
    mutationFn: (patch: JobPortalUpdate) => jobPortalApi.update(portal.key, patch),
    // Optimistic: the switch moves at once and goes back if the save fails.
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: PORTALS_KEY })
      const before: JobPortalUpdate = {}
      for (const field of Object.keys(patch) as Array<keyof JobPortalUpdate>) {
        before[field] = portal[field]
      }
      patchPortal(queryClient, portal.key, patch)
      return { before }
    },
    onSuccess: (saved) => putPortal(queryClient, saved),
    onError: (error: ApiError, _patch, context) => {
      if (context?.before) patchPortal(queryClient, portal.key, context.before)
      toast.error(`Could not update ${portal.name}`, error.message)
    },
  })

  const connect = useMutation({
    mutationFn: () => jobPortalApi.connect(portal.key),
    onSuccess: (saved) => {
      putPortal(queryClient, saved)
      onWatch(saved?.key ? saved : portal, 'connect')
      toast.info(
        `Setting up ${portal.name}`,
        'We sign in or create your account in the background. This can take a few minutes.',
      )
    },
    onError: (error: ApiError) => toast.error(`Could not connect ${portal.name}`, error.message),
  })

  const disconnect = useMutation({
    mutationFn: () => jobPortalApi.disconnect(portal.key),
    onSuccess: (saved) => {
      putPortal(queryClient, saved)
      toast.info(`${portal.name} disconnected`)
    },
    onError: (error: ApiError) => toast.error(`Could not disconnect ${portal.name}`, error.message),
  })

  const sync = useMutation({
    mutationFn: () => jobPortalApi.sync(portal.key),
    onSuccess: (result) => {
      putPortal(queryClient, result.portal)
      if (result.queued) {
        onWatch(result.portal?.key ? result.portal : portal, 'sync')
        toast.info('Fetching in the background…', `${portal.name} jobs show up in your feed as they arrive.`)
        return
      }
      refreshJobLists(queryClient)
      toast.success(
        `Found ${result.jobsFound ?? 0}, added ${result.jobsImported ?? 0}`,
        result.jobsImported
          ? `New ${portal.name} jobs are in your feed.`
          : `Nothing new on ${portal.name} since the last check.`,
      )
    },
    onError: (error: ApiError) => toast.error(`Could not fetch ${portal.name} jobs`, error.message),
  })

  const rehearse = useMutation({
    mutationFn: () => portalExtras.rehearse(portal.key),
    onSuccess: (saved) => {
      putPortal(queryClient, saved)
      onWatch(saved?.key ? saved : portal, 'rehearse')
      toast.info(`Testing ${portal.name}`, 'We walk the site without sending anything. What we would do shows here in a few minutes.')
    },
    onError: (error: ApiError) => toast.error(`Could not start the test run on ${portal.name}`, error.message),
  })

  const remove = useMutation({
    mutationFn: () => jobPortalApi.remove(portal.key),
    onSuccess: () => {
      queryClient.setQueryData<JobPortal[]>(PORTALS_KEY, (current) =>
        Array.isArray(current) ? current.filter((entry) => entry.key !== portal.key) : current,
      )
      refreshJobLists(queryClient)
      toast.info(`${portal.name} removed`)
    },
    onError: (error: ApiError) => toast.error(`Could not remove ${portal.name}`, error.message),
  })

  return { update, connect, disconnect, sync, rehearse, remove }
}

type PortalActions = ReturnType<typeof usePortalActions>

/** The one word for where a portal stands, and its colour. */
function portalPill(portal: JobPortal, account: boolean): { label: string; tone: Tone; live?: boolean } {
  if (portal.status === 'connecting') return { label: 'Connecting', tone: 'info', live: true }
  if (portal.status === 'needs_you') return { label: 'Needs you', tone: 'warning' }
  if (portal.status === 'error') return { label: 'Error', tone: 'danger' }
  if (!account) return { label: 'Ready', tone: 'success' }
  if (portal.status === 'connected') return { label: 'Connected', tone: 'success' }
  return { label: 'Not connected', tone: 'neutral' }
}

function accountLabel(portal: JobPortal, account: boolean, optionalAccount: boolean): string {
  if (portal.kind === 'feed') return 'Open listings · no account'
  if (account) return 'Account needed'
  if (optionalAccount) return 'Account only to apply'
  return 'No account needed'
}

function PortalRow({
  portal,
  fetching,
  onWatch,
}: {
  portal: JobPortal
  /** A fetch for this portal is running in the background. */
  fetching: boolean
  onWatch: (portal: JobPortal, kind: Watch['kind']) => void
}) {
  const phone = useIsPhone()
  const actions = usePortalActions(portal, onWatch)
  const account = usesAccount(portal)
  const optionalAccount = accountToApply(portal)
  const accountish = account || (optionalAccount && portal.status !== 'not_connected')
  const pill = portalPill(portal, accountish)
  const [open, setOpen] = useState(false)
  const inline = open && !phone

  const details = (
    <PortalDetails portal={portal} fetching={fetching} actions={actions} account={account} optionalAccount={optionalAccount} />
  )

  return (
    <li className={cn('min-w-0', inline && 'bg-surface-muted/40')}>
      <div className="flex items-center gap-2 px-3 py-2.5 sm:gap-3 sm:px-5 sm:py-3">
        {/* The row is the tap target: a sheet on a phone, the details below it on a desktop. */}
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-field px-1 text-left transition-colors hover:bg-surface-muted/70"
        >
          <span
            aria-hidden
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-[13px] font-semibold tracking-tight text-brand"
          >
            {initials(portal.name)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate text-[15px] font-semibold leading-snug text-ink">{portal.name}</span>
              {portal.region && <span className="text-[11.5px] font-medium uppercase tracking-wide text-ink-faint">{portal.region}</span>}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-ink-muted">
              <StatusPill size="sm" tone={pill.tone} live={pill.live} className="md:hidden">
                {pill.label}
              </StatusPill>
              <span className="truncate">{hostOf(portal.url)}</span>
              <span className="hidden sm:inline" aria-hidden>
                ·
              </span>
              <span className="hidden sm:inline">{accountLabel(portal, account, optionalAccount)}</span>
            </span>
          </span>
          <StatusPill tone={pill.tone} live={pill.live} className="hidden md:inline-flex">
            {pill.label}
          </StatusPill>
          <Chevron open={inline} className="hidden md:block" />
        </button>
        <Switch
          checked={portal.selected}
          onChange={(checked) => actions.update.mutate({ selected: checked })}
          label={`Use ${portal.name}`}
        />
      </div>

      {inline && <div className="border-t border-line px-4 py-4 sm:px-5 md:pl-[76px]">{details}</div>}

      <Sheet
        open={open && phone}
        onClose={() => setOpen(false)}
        title={portal.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <StatusPill size="sm" tone={pill.tone} live={pill.live}>
              {pill.label}
            </StatusPill>
            <span>{hostOf(portal.url)}</span>
          </span>
        }
      >
        <div className="pb-2">
          <div className="mb-4 flex min-h-[44px] items-center justify-between gap-3 rounded-field border border-line px-3.5 py-2">
            <span className="text-[14px] text-ink">Use {portal.name}</span>
            <Switch
              checked={portal.selected}
              onChange={(checked) => actions.update.mutate({ selected: checked })}
              label={`Use ${portal.name}`}
            />
          </div>
          {details}
        </div>
      </Sheet>
    </li>
  )
}

/** Where the portal stands, what can be done about it, and its two options. */
function PortalDetails({
  portal,
  fetching,
  actions,
  account,
  optionalAccount,
}: {
  portal: JobPortal
  fetching: boolean
  actions: PortalActions
  account: boolean
  optionalAccount: boolean
}) {
  const { update, connect, disconnect, sync, rehearse, remove } = actions
  const canConnect =
    (account || optionalAccount) && (portal.status === 'not_connected' || portal.status === 'error')
  const canFetch = !account || portal.status === 'connected'
  const jobCount = portal.jobCount ?? 0
  const retry = (account || optionalAccount) && (portal.status === 'needs_you' || portal.status === 'error')
  /** Signed in, but the profile stopped short: connecting again picks it up where it left off. */
  const unfinishedProfile =
    (account || optionalAccount) &&
    portal.accountProfile === 'partial' &&
    (portal.status === 'connected' || portal.status === 'needs_you')
  const busy = portal.status === 'connecting'
  const [ownAccount, setOwnAccount] = useState(false)
  const queryClient = useQueryClient()

  if (!portal.selected) {
    return (
      <div className="space-y-3">
        <p className="text-[13.5px] text-ink-muted">
          Switched off. Turn it on to bring {portal.name} jobs into your feed{portal.canApplyOnPortal ? ' and apply there' : ''}.
        </p>
        {portal.note && <p className="text-[12.5px] leading-relaxed text-ink-muted">{portal.note}</p>}
        <PortalLinks portal={portal} />
        {!portal.builtIn && (
          <RemoveControl name={portal.name} onRemove={() => remove.mutate()} removing={remove.isPending} />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <StatusLine portal={portal} account={account || (optionalAccount && portal.status !== 'not_connected')} />
      {portal.note && <p className="text-[12.5px] leading-relaxed text-ink-muted">{portal.note}</p>}

      {(canConnect || canFetch || retry || unfinishedProfile || portal.status === 'error') && (
        <div className="flex flex-wrap items-center gap-2">
          {portal.status === 'needs_you' && portal.handoffUrl && (
            <a
              href={portal.handoffUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-field bg-ink px-3.5 text-[13px] font-medium text-oncolor transition-colors hover:bg-ink/90 md:h-9"
            >
              Finish this step
              <ExternalIcon />
            </a>
          )}
          {unfinishedProfile && (
            <Button size="sm" onClick={() => connect.mutate()} loading={connect.isPending} disabled={busy}>
              Finish profile setup
            </Button>
          )}
          {retry && !unfinishedProfile && (
            <Button
              size="sm"
              variant={portal.status === 'error' && !optionalAccount ? 'primary' : 'secondary'}
              onClick={() => connect.mutate()}
              loading={connect.isPending}
              disabled={busy}
            >
              Try again
            </Button>
          )}
          {canFetch && optionalAccount && canConnect && portal.status !== 'error' && (
            <Button size="sm" variant="secondary" onClick={() => connect.mutate()} loading={connect.isPending}>
              Create account to apply here
            </Button>
          )}
          {canConnect && !optionalAccount && portal.status !== 'error' && (
            <Button size="sm" onClick={() => connect.mutate()} loading={connect.isPending}>
              {portal.hasOwnCredentials ? 'Sign in with my account' : 'Create account / Sign in'}
            </Button>
          )}
          {canFetch && (
            <Button size="sm" variant={canConnect ? 'secondary' : 'primary'} onClick={() => sync.mutate()} loading={sync.isPending} disabled={fetching}>
              Fetch jobs now
            </Button>
          )}
          {(account || optionalAccount) && !busy && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => rehearse.mutate()}
              loading={rehearse.isPending}
              title="Walks the site the way a real connection would, and sends nothing"
            >
              Test run (nothing sent)
            </Button>
          )}
          {(account || optionalAccount) && !busy && (
            <Button size="sm" variant="ghost" onClick={() => setOwnAccount((value) => !value)} aria-expanded={ownAccount}>
              {portal.hasOwnCredentials ? 'Change my login' : 'Use my existing account'}
            </Button>
          )}
          {canFetch && fetching && (
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted" role="status">
              <Spinner />
              Fetching in the background…
            </span>
          )}
        </div>
      )}

      {ownAccount && (
        <OwnAccountForm
          portal={portal}
          onSaved={(saved) => {
            putPortal(queryClient, saved)
            setOwnAccount(false)
          }}
        />
      )}

      {portal.lastRehearsal && (
        <p className="text-[12.5px] leading-relaxed text-ink-muted" role="status">
          <span className="font-medium text-ink-soft">Test run {relativeTime(portal.lastRehearsal.at)}:</span>{' '}
          {portal.lastRehearsal.humanCheck
            ? portal.lastRehearsal.humanCheck
            : portal.lastRehearsal.stopReason
              ? `${plural(portal.lastRehearsal.screens, 'screen')} read, stopped where it ${portal.lastRehearsal.stopReason}.`
              : `${plural(portal.lastRehearsal.screens, 'screen')} read. Nothing was sent.`}
        </p>
      )}

      <div className="panel divide-y divide-line">
        <OptionRow
          label="Bring matching jobs into my feed"
          checked={portal.fetchJobs}
          onChange={(checked) => update.mutate({ fetchJobs: checked })}
        />
        {portal.canApplyOnPortal && (
          <OptionRow
            label="Apply on this portal"
            checked={portal.applyOnPortal}
            onChange={(checked) => update.mutate({ applyOnPortal: checked })}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className="min-w-0 text-[12px] leading-relaxed text-ink-muted">
          <span className="num">
            {portal.lastSyncedAt ? `Last checked ${relativeTime(portal.lastSyncedAt)}` : 'Not checked yet'}
          </span>
          <span aria-hidden className="mx-1.5 text-ink-faint">
            ·
          </span>
          <Link
            to={`/dashboard/jobs?board=${encodeURIComponent(portal.key)}`}
            className="num whitespace-nowrap font-medium text-ink-soft underline decoration-line-strong underline-offset-2 transition-colors hover:text-brand"
          >
            {plural(jobCount, 'job')}
          </Link>
          <span aria-hidden className="mx-1.5 text-ink-faint">
            ·
          </span>
          <span className="text-ink-faint">{scriptLine(portal)}</span>
        </p>
        <PortalLinks portal={portal} />
        {((account && portal.status === 'connected') || !portal.builtIn) && (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {account && portal.status === 'connected' && (
              <Button size="xs" variant="ghost" onClick={() => disconnect.mutate()} loading={disconnect.isPending}>
                {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
              </Button>
            )}
            {!portal.builtIn && (
              <RemoveControl name={portal.name} onRemove={() => remove.mutate()} removing={remove.isPending} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/** The portal's own site, in a new tab. */
function PortalLinks({ portal }: { portal: JobPortal }) {
  return (
    <a
      href={portal.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-[32px] min-w-0 max-w-full items-center gap-1 text-[12.5px] text-ink-muted transition-colors hover:text-brand"
    >
      <span className="truncate">Open {hostOf(portal.url)}</span>
      <ExternalIcon />
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  )
}

function Chevron({ open, className }: { open: boolean; className?: string }) {
  return (
    <svg
      className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform', open && 'rotate-180', className)}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function scriptLine(portal: JobPortal): string {
  const script = portal.script
  if (script?.learnedAt) return `Script: learned ${formatDate(script.learnedAt)}`
  if (script?.fetch === 'ready' || script?.account === 'ready') return 'Script: ready'
  return 'Script: will be learned on first run'
}

/**
 * The user's own login on the portal: we sign in with it instead of making an
 * account with the application address. The password goes to the server
 * encrypted at rest and is never shown again — the field always starts empty.
 */
function OwnAccountForm({ portal, onSaved }: { portal: JobPortal; onSaved: (saved: JobPortal) => void }) {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => portalExtras.credentials(portal.key, { identifier: identifier.trim(), password }),
    onSuccess: (saved) => {
      setPassword('')
      setIdentifier('')
      setError(null)
      onSaved(saved)
      toast.success(`${portal.name} login saved`, 'Press Sign in with my account to connect with it.')
    },
    onError: (failure: ApiError) => {
      setError(failure.fieldErrors?.identifier ?? failure.fieldErrors?.password ?? failure.message)
    },
  })
  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        if (!identifier.trim() || !password) {
          setError('Enter the email or username and the password you use on this portal.')
          return
        }
        save.mutate()
      }}
      className="panel space-y-3 border-dashed border-line-strong p-3.5"
      aria-label={`Your own ${portal.name} account`}
    >
      <p className="text-[13px] leading-relaxed text-ink-soft">
        Already have a {portal.name} account? We will sign in with it instead of creating one.
        {portal.hasOwnCredentials && portal.accountIdentifier && (
          <>
            {' '}
            Saved login: <span className="font-medium text-ink">{portal.accountIdentifier}</span>.
          </>
        )}
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        <TextField
          label="Email or username"
          appearance="box"
          autoComplete="off"
          value={identifier}
          onChange={(event) => {
            setIdentifier(event.target.value)
            if (error) setError(null)
          }}
          maxLength={320}
        />
        <TextField
          label="Password"
          appearance="box"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
            if (error) setError(null)
          }}
          maxLength={200}
        />
      </div>
      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" type="submit" loading={save.isPending} className="w-full sm:w-auto">
          Save login
        </Button>
        <span className="text-[12px] text-ink-muted">Stored encrypted. We never show it again.</span>
      </div>
    </form>
  )
}

/** A coloured dot and one sentence: where this portal stands right now. */
function StatusLine({ portal, account }: { portal: JobPortal; account: boolean }) {
  let dot: ReactNode
  let text: ReactNode
  let tone = 'text-ink-soft'

  if (portal.status === 'connecting') {
    dot = <Spinner />
    text = 'Setting up your account… this can take a few minutes'
  } else if (portal.status === 'needs_you') {
    dot = <StatusDot className="bg-warning" />
    tone = 'text-warning'
    text = (
      <>
        <span className="font-medium">Needs you:</span>{' '}
        {portal.lastError || 'one step has to be finished by hand.'}
      </>
    )
  } else if (portal.status === 'error') {
    dot = <StatusDot className="bg-danger" />
    tone = 'text-danger'
    text = portal.lastError || 'Something went wrong. Try again.'
  } else if (!account) {
    // Open listings have no account to connect: they are ready as they are.
    dot = <StatusDot className="bg-success" />
    text = 'Ready · no account needed'
  } else if (portal.status === 'connected') {
    dot = <StatusDot className="bg-success" />
    text = (
      <>
        <span className="font-medium text-success">Connected</span>
        {portal.accountIdentifier && (
          <span className="text-ink-muted"> · {portal.accountIdentifier}</span>
        )}
      </>
    )
  } else {
    dot = <StatusDot className="bg-line-strong" />
    tone = 'text-ink-muted'
    text = 'Not connected yet'
  }

  return (
    <p
      className={cn('flex items-start gap-2 text-[13.5px] leading-relaxed', tone)}
      role="status"
      aria-live="polite"
    >
      <span className="flex h-[21px] shrink-0 items-center">{dot}</span>
      <span className="min-w-0 break-words">{text}</span>
    </p>
  )
}

function StatusDot({ className }: { className: string }) {
  return <span aria-hidden className={cn('h-2 w-2 rounded-full', className)} />
}

function OptionRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex min-h-[44px] items-center justify-between gap-3 px-3.5 py-2">
      <span className="min-w-0 text-[13.5px] text-ink-soft">{label}</span>
      <Switch size="sm" checked={Boolean(checked)} onChange={onChange} label={label} />
    </div>
  )
}

/** "Remove", then one more click to confirm: a custom portal's script goes with it. */
function RemoveControl({
  name,
  onRemove,
  removing,
}: {
  name: string
  onRemove: () => void
  removing: boolean
}) {
  const [confirming, setConfirming] = useState(false)

  if (!confirming && !removing) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex h-8 items-center rounded-[8px] px-2.5 text-[12.5px] font-medium text-danger transition-colors hover:bg-danger/10"
      >
        Remove
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 text-[12.5px]">
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        aria-label={`Remove ${name}`}
        className="inline-flex h-8 items-center rounded-[8px] bg-danger/10 px-2.5 font-medium text-danger transition-colors hover:bg-danger/15 disabled:opacity-55"
      >
        {removing ? 'Removing…' : 'Yes, remove'}
      </button>
      {!removing && (
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="inline-flex h-8 items-center rounded-[8px] px-2.5 text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
        >
          Cancel
        </button>
      )}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Any other portal, by its URL
// ---------------------------------------------------------------------------
function AddPortalCard({ onWatch }: { onWatch: (portal: JobPortal, kind: Watch['kind']) => void }) {
  const queryClient = useQueryClient()
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const add = useMutation({
    mutationFn: () => jobPortalApi.add({ url: url.trim(), name: name.trim() || undefined }),
    onSuccess: (saved) => {
      putPortal(queryClient, saved)
      // The first run (learning the script, reading the first jobs) is in the background: follow it so its outcome shows.
      if (saved?.key && saved.selected) onWatch(saved, 'sync')
      setUrl('')
      setName('')
      setError(null)
      toast.success(
        `${saved?.name ?? 'Portal'} added`,
        saved?.selected
          ? 'The first run learns how to read it.'
          : 'Switch it on to start using it.',
      )
    },
    onError: (failure: ApiError) => {
      setError(failure.fieldErrors?.url ?? failure.message)
      toast.error('Could not add the portal', failure.message)
    },
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const problem = urlProblem(url.trim())
    setError(problem)
    if (!problem) add.mutate()
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      aria-labelledby="portals-add-title"
      className="card border-dashed border-line-strong p-4 sm:p-6"
    >
      <h3 id="portals-add-title" className="text-[15px] font-semibold tracking-tight text-ink">
        Add another portal
      </h3>
      <div className="mt-4 grid items-end gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
        <TextField
          label="Portal URL"
          appearance="box"
          type="url"
          inputMode="url"
          autoComplete="off"
          required
          placeholder="https://example.com/jobs"
          value={url}
          onChange={(event) => {
            setUrl(event.target.value)
            if (error) setError(null)
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby="portals-add-help"
          className={cn(error && 'border-danger')}
        />
        <TextField
          label="Name (optional)"
          appearance="box"
          autoComplete="off"
          maxLength={60}
          placeholder="e.g. Otta"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Button type="submit" loading={add.isPending} className="w-full md:w-auto">
          Add portal
        </Button>
      </div>
      <div id="portals-add-help" className="mt-2.5">
        {error && (
          <p role="alert" className="mb-1 text-[13px] text-danger">
            {error}
          </p>
        )}
        <p className="text-[13px] leading-relaxed text-ink-muted">
          Paste the portal's job search page. The first run learns how to read it; later runs reuse
          that script.
        </p>
      </div>
    </form>
  )
}

function urlProblem(text: string): string | null {
  if (!text) return "Enter the portal's address."
  if (!/^https?:\/\//i.test(text)) return 'The address must start with http:// or https://'
  try {
    if (!new URL(text).hostname.includes('.')) return 'That does not look like a full web address.'
  } catch {
    return 'That does not look like a full web address.'
  }
  return null
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------
function ExternalIcon() {
  return (
    <svg className="h-3 w-3 shrink-0" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M4.5 2.5h5v5M9.5 2.5 3 9"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-muted" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}
